import { useState } from "react";
import {
  Box,
  InputGroup,
  Input,
  InputRightElement,
  useToast,
  Button,
  FormLabel,
  FormErrorMessage,
  FormControl,
  Flex,
  Text,
  Heading,
  Image,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";

import Api from "src/Config";

const validation = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Required")
    .matches(/\.[a-zA-Z]+$/, "Invalid email address"),
  password: Yup.string().required("Required"),
});

const DemoLogin = () => {
  const toast = useToast();
  const [isToggle, setIsToggle] = useState(false);

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.post("auth/account/login-v2", payload);
      return data;
    },
    onError: (err: any) => {
      throw err;
    },
  });

  const handleLogin = async (data: any) => {
    const payload = {
      email: data.email,
      password: data.password,
      host: process.env.REACT_APP_HOST,
    };
    try {
      const response = await mutateAsync(payload);

      if (response) {
        // window.location.href = `${process.env.REACT_APP_RADAR_CLIENT}?session=${response.data.data.sso_session}`;
        // set login with attribute in local storage called with-radar
        localStorage.setItem("login-with", "radar");
        console.log("response", response.data?.data?.data?.tokens?.access);
        window.location.href = `${window?.location?.origin}?token=${response.data?.data?.data?.tokens?.access}&login_with=radar`;
      }
    } catch (error) {
      toast({
        description: "An error occurred, Please try again",
        status: "error",
        duration: 9000,
        isClosable: true,
        variant: "left-accent",
      });
    }
  };

  const handleToggle = () => {
    setIsToggle(!isToggle);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: handleLogin,
    validationSchema: validation,
  });

  const { handleBlur, handleChange, handleSubmit, values, errors } = formik;

  return (
    <Flex
      height="100vh"
      width="100vw"
      position="fixed"
      top={0}
      left={0}
      zIndex={99999}
      direction={{ base: "column", lg: "row" }}
    >
      {/* Left Side - Image Only */}
      <Box
        flex="1"
        bg="#fff"
        position="relative"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        color="white"
        p={0}
        minH={{ base: "40vh", lg: "100vh" }}
      >
        <Image
          src="https://account.prembly.com/prembly-3efw.png"
          alt="Prembly Dashboard"
          width="90%"
          height="auto"
          objectFit="contain"
          borderRadius="12px"
          boxShadow="2xl"
        />
      </Box>

      {/* Right Side - Login Form */}
      <Box
        flex="1"
        bg="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: 6, md: 8 }}
        py={{ base: 8, lg: 0 }}
        minH={{ base: "100vh", lg: "auto" }}
      >
        <Box width="100%" maxW="400px">
          {/* Prembly Logo */}
          <VStack spacing={6} mb={8}>
            <HStack spacing={2} alignItems="center">
              <Box width="8px" height="8px" bg="teal.500" borderRadius="full" />
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                prembly
              </Text>
            </HStack>

            <VStack spacing={2}>
              <Heading
                fontSize="2xl"
                fontWeight="semibold"
                color="gray.800"
                textAlign="center"
              >
                Welcome to Prembly
              </Heading>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Kindly fill in your details to sign in to your account
              </Text>
            </VStack>
          </VStack>

          {/* Error Message */}
          {error && (
            <Box
              mb={4}
              p={3}
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              borderRadius="md"
            >
              <Text fontSize="sm" color="red.600" textAlign="center">
                {error?.response?.status === 500 &&
                  "An error occurred, Please try again"}
                {error?.response?.status === 400 &&
                  error?.response?.data?.detail}
              </Text>
            </Box>
          )}

          {/* Login Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <VStack spacing={4}>
              <FormControl
                isInvalid={errors.email ? true : false}
                isRequired={true}
              >
                <FormLabel
                  fontSize="sm"
                  color="gray.700"
                  fontWeight="normal"
                  mb={2}
                >
                  Email Address
                </FormLabel>
                <Input
                  value={values.email}
                  type="email"
                  placeholder="name@company.com"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="email"
                  size="lg"
                  bg="gray.50"
                  borderColor="gray.200"
                  borderRadius="md"
                  _hover={{ borderColor: "gray.300", bg: "white" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px #319795",
                    bg: "white",
                  }}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={errors.password ? true : false}
                isRequired={true}
              >
                <FormLabel
                  fontSize="sm"
                  color="gray.700"
                  fontWeight="normal"
                  mb={2}
                >
                  Password
                </FormLabel>
                <InputGroup size="lg">
                  <Input
                    value={values.password}
                    name="password"
                    type={!isToggle ? "password" : "text"}
                    placeholder="••••••••"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    bg="gray.50"
                    borderColor="gray.200"
                    borderRadius="md"
                    _hover={{ borderColor: "gray.300", bg: "white" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px #319795",
                      bg: "white",
                    }}
                  />
                  <InputRightElement width="3rem">
                    {!isToggle ? (
                      <MdOutlineVisibility
                        onClick={handleToggle}
                        cursor="pointer"
                        size="20px"
                        color="#718096"
                      />
                    ) : (
                      <MdOutlineVisibilityOff
                        onClick={handleToggle}
                        cursor="pointer"
                        size="20px"
                        color="#718096"
                      />
                    )}
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>

              <Button
                width="100%"
                size="lg"
                bg="#2D7D7B"
                color="white"
                isLoading={isPending}
                isDisabled={
                  isPending ||
                  !(values.email && values.password) ||
                  !!errors.email ||
                  !!errors.password
                }
                onClick={() => handleSubmit()}
                _hover={{ bg: "#1A5F5D" }}
                _active={{ bg: "#164A48" }}
                mt={6}
                borderRadius="md"
                fontWeight="medium"
                fontSize="md"
              >
                Log In
              </Button>
            </VStack>
          </form>
        </Box>
      </Box>
    </Flex>
  );
};

export default DemoLogin;
